import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskCartLoyaltyBlockConfig } from '@front-platform/content-builder';
import { kioskCartLoyaltyBlockResolveConfig } from '@front-platform/content-builder';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  computed,
  ViewChild,
  input,
} from '@angular/core';
import { find } from 'lodash';

import { ComoService } from '@core/services/como/como.service';

import { ComoAsset, ComoUser } from '@core/models/como.model';
import { CartService } from '@core/services/cart/cart.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
export interface KioskCartLoyaltyBlockRuntimeConfig {
  'display_credits'?: boolean;
  'display_points'?: boolean;
  'display_redeemable_gifts'?: boolean;
  'display_not_redeemable_gifts'?: boolean;
  'display_redeemable_point_shop'?: boolean;
  'display_not_redeemable_point_shop'?: boolean;
  'display_not_usable_point_shop'?: boolean;
}

export type KioskCartLoyaltyBlockConfigInput = Partial<
  Omit<KioskCartLoyaltyBlockConfig, keyof KioskCartLoyaltyBlockRuntimeConfig> &
    KioskCartLoyaltyBlockRuntimeConfig
>;
export type KioskCartLoyaltyBlockResolvedConfig = Omit<
  KioskCartLoyaltyBlockConfig,
  keyof KioskCartLoyaltyBlockRuntimeConfig
> &
  Partial<KioskCartLoyaltyBlockRuntimeConfig>;
export interface KioskCartLoyaltyBlockContract extends ConfigComponentContract<
  KioskCartLoyaltyBlockConfigInput,
  KioskCartLoyaltyBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-cart-loyalty-block',
  templateUrl: './kiosk-cart-loyalty-block.component.html',
  standalone: false,
})
export class KioskCartLoyaltyBlockComponent
  extends DestroyerBase
  implements OnInit, OnDestroy, KioskCartLoyaltyBlockContract
{
  readonly config = input<KioskCartLoyaltyBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskCartLoyaltyBlockConfig,
    KioskCartLoyaltyBlockRuntimeConfig
  >(kioskCartLoyaltyBlockResolveConfig, () => ({ config: this.config() }));
  get primaryButtonBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['primary_button_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get primaryButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['primary_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get secondaryButtonBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['secondary_button_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get secondaryButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['secondary_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get textColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor;
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }
  get connexionHeading(): string | undefined {
    return this.resolvedConfig()['connexion_heading'] as unknown as string | undefined;
  }
  get connexionCardLabel(): string | undefined {
    return this.resolvedConfig()['connexion_card_label'] as unknown as string | undefined;
  }
  get linkConnexion(): string | undefined {
    return this.resolvedConfig()['link_connexion'] as unknown as string | undefined;
  }

  get heading(): string {
    return this.resolvedConfig()['heading'] as unknown as string;
  }
  get subheading(): string | undefined {
    return this.resolvedConfig()['subheading'] as unknown as string | undefined;
  }
  get displayCredits(): boolean {
    const value = this.resolvedConfig()['display_credits'];
    return value === undefined ? true : (value as unknown as boolean);
  }
  get displayPoints(): boolean {
    const value = this.resolvedConfig()['display_points'];
    return value === undefined ? true : (value as unknown as boolean);
  }
  get displayRedeemableGifts(): boolean {
    const value = this.resolvedConfig()['display_redeemable_gifts'];
    return value === undefined ? true : (value as unknown as boolean);
  }
  get displayNotRedeemableGifts(): boolean {
    const value = this.resolvedConfig()['display_not_redeemable_gifts'];
    return value === undefined ? true : (value as unknown as boolean);
  }
  get displayRedeemablePointShop(): boolean {
    const value = this.resolvedConfig()['display_redeemable_point_shop'];
    return value === undefined ? true : (value as unknown as boolean);
  }
  get displayNotRedeemablePointShop(): boolean {
    const value = this.resolvedConfig()['display_not_redeemable_point_shop'];
    return value === undefined ? true : (value as unknown as boolean);
  }
  get displayNotUsablePointShop(): boolean {
    const value = this.resolvedConfig()['display_not_usable_point_shop'];
    return value === undefined ? true : (value as unknown as boolean);
  }

  @ViewChild('loyaltyCarousel') loyaltyCarousel!: ElementRef;

  scrollAmount = 172; // w-40 + mr-3

  public comoUser = signal<ComoUser | null | undefined>(undefined);

  creditBalance = computed(() => this.comoUser()?.credit_balance?.monetary ?? 0);
  pointsBalance = computed(() => this.comoUser()?.points_balance?.non_monetary ?? 0);

  private readonly destroyed$ = new Subject<boolean>();

  redeemableAssets = computed(() => {
    const user = this.comoUser();
    if (!user) return [];
    return user.assets.filter(asset => {
      const isPointShop = asset.key?.startsWith('ps_');
      return (
        asset.is_usable &&
        asset.status?.toLowerCase() === 'active' &&
        asset.redeemable === true &&
        !isPointShop
      );
    });
  });

  noRedeemableAssets = computed(() => {
    const user = this.comoUser();
    if (!user) return [];
    return user.assets.filter(asset => {
      const isPointShop = asset.key?.startsWith('ps_');
      return (
        asset.is_usable &&
        asset.status?.toLowerCase() === 'active' &&
        asset.redeemable !== true &&
        !isPointShop
      );
    });
  });

  redeemablePointShops = computed(() => {
    const user = this.comoUser();
    if (!user) return [];
    return user.assets.filter(asset => {
      const isPointShop = asset.key?.startsWith('ps_');
      return (
        asset.is_usable &&
        asset.status?.toLowerCase() === 'active' &&
        asset.redeemable === true &&
        isPointShop
      );
    });
  });

  noRedeemablePointShops = computed(() => {
    const user = this.comoUser();
    if (!user) return [];
    return user.assets.filter(asset => {
      const isPointShop = asset.key?.startsWith('ps_');
      return (
        asset.is_usable &&
        asset.status?.toLowerCase() === 'active' &&
        asset.redeemable !== true &&
        isPointShop
      );
    });
  });

  pointShops = computed(() => {
    const user = this.comoUser();
    if (!user) return [];
    return user.assets.filter(asset => !asset.is_usable);
  });

  positionSticky = false;
  positionStickyTop = 0;

  constructor(
    private readonly comoService: ComoService,
    private readonly cartService: CartService,
    private readonly orderDispatcher: OrderStoreDispatcher,
  ) {
    super();
  }

  ngOnInit(): void {
    this.comoService.loadComoUser();
    this.comoService
      .getComoUser$()
      .pipe(
        takeUntil(this.destroyed$),
        tap(comoUser => this.comoUser.set(comoUser)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onAssetAdd(asset: ComoAsset): void {
    this.comoService.confirmUsePointToAddAsset(asset, true);
  }

  onAssetRemove($event: ComoAsset): void {
    const coupon = { key: $event.key };
    this.cartService.removeCoupon(coupon);
    this.updateCart();
  }

  private updateCart(): void {
    this.orderDispatcher.checkCartIsValid();
  }
  isAssetInCart$(asset: ComoAsset): Observable<boolean> {
    return this.comoService.getComoUser$().pipe(
      takeUntil(this._destroyerRef),
      map(() => {
        const coupons = this.cartService.getCouponsInCart();

        return !!find(coupons, coupon => coupon.key && coupon.key === asset.key);
      }),
    );
  }

  scrollLeft() {
    this.loyaltyCarousel.nativeElement.scrollLeft -= this.scrollAmount * 2;
  }

  scrollRight() {
    this.loyaltyCarousel.nativeElement.scrollLeft += this.scrollAmount * 2;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCartLoyaltyBlockComponent, { 'keyAliases': {} });
